/*
 * Template
 * {
 * version: '',
 * date: '',
 * added: `<MARKDOWN TEXT>`,
 * fixed: `<MARKDOWN TEXT>`,
 * improved: `<MARKDOWN TEXT>`
 * },
 */

const RELEASES = [
  {
    version: '1.7.0',
    date: 'November, 2024',
    added: `
- Introduced a new Two-Credential Authentication rule for customers with specific role requirements for access. Admins can now configure whether access requires authentication from any two users or if one of the two must be an administrator. A Group or Account Admin fulfills this rule. This feature provides an optional, additional check for user roles before granting access to critical safes.

- Firmware version has been updated to 1.3.14 for this release.
    `,
    fixed: `
- The Add Feedback popup prompted to the user upon adding their first device and the user, had misaligned icons on the smiley faces.

- Fixed a bug that incorrectly triggered a device reboot when updating the device name and account settings. Now, only configuration setting changes on the device will prompt a reboot.

- Some customers would occasionally encounter an issue that caused safes with biometric devices to not function and required a restart of the device and a re-synchronization with the cloud.

- Fixed a rare issue where WiFi network connectivity could lead to system stability problems.

- Corrected an issue where two-credential authentication attempts following a cancellation sometimes caused door open/close events to not appear in the Physical Access Log.
    `,
    improved: `
- A token refresh feature has been added to the web client, ensuring that users stay logged in if they are actively using the Link web client.

- Additional text has been included for clarity when deleting a primary credential.

- The informational tab in Settings titled “ACM Sync” is no longer available.

- Introduced parameters and thresholds to prevent devices from requesting new credentials unnecessarily. This reduces the chance of a full download under poor network conditions. The following conditions now trigger this:
  - Latency of 675 ms or higher.

  - Network packet loss to the cloud exceeds 40%.

- Enhanced the Emergency firmware capability on devices.

- Other logging and support-related improvements have been implemented for better device monitoring and management.
    `,
  },
  {
    version: '1.6.8',
    date: 'June, 2024',
    improved: `
- Enhanced the device certificate renewal process. Certificates are essential for securely authenticating devices with the cloud platform.
    `,
  },
  {
    version: '1.6.7',
    date: 'May, 2024',
    fixed: `
- Resolved an issue where delete device did not previously prevent multiple clicks which could cause it to be triggered twice registering multiple attempts to delete the same device.

- Fixed an issue where deleting a credential (such as Card or UserID) did not update the credential status image in User management without refreshing.
    `,
    improved: `
- The Add Device flow has been enhanced to prioritize asking for the operation mode for the device (Door, Cabinet, or Safe) instead of Mac Address. Specifically for Safe devices, a unique flow and design have been implemented, featuring text changes and new images. This adjustment caters to MedixSafe users' unique use cases, ensuring the user interface aligns with their needs.

- Various enhancements have been made to the Group Management interface.

  - Users and devices that are already added to a Group will now be identified with a new column called “Current Group”.

  - If devices are selected for a new group while already existing in another group, there will be a warning alerting the user to the the device conflict scenario and proceeding will reassign from current group.

  - A new checkbox facilitates easier multi-selection for both Users and Device selection.

  - Email validation has been added for Group Alert Emails.

  - Improved visibility: Selected users and devices now have increased color contrast for better distinction.

  - Minor text updates throughout.

- The Mobile Two-Factor Authentication Schedule feature has been integrated under Authenticators, eliminating its standalone page. This change aims to reduce confusion, as the scheduling option is relevant only when a mobile authenticator is being used for second-factor authentication on the account. Despite this integration, all scheduling functionalities remain accessible whenever a mobile authenticator is activated.
    `,
  },
  {
    version: '1.6.6',
    date: 'April, 2024',
    added: `
- We've introduced a new mode called PIN Fallback to the existing authentication method of Card/UserID + Fingerprint. You can find this option when adding or editing a Safe operation mode device only.

  - When activated, users without enrolled fingerprints but with associated PINs linked to their Card or UserID can authenticate using PIN as a second factor. Users with fingerprint templates will continue to authenticate using their biometrics.

  - This mode addresses users who may have fingers unsuitable for fingerprint authentication. PIN ensures compliance with 2FA requirements while providing access to the safe. Biometrics remains the primary mode, with users defaulting to it if already enrolled.

  - Access Log will identify if this authentication mode was used if an user authenticates with Card/UserID + PIN due to fallback.

  - Note: Firmware version must be higher than 1.2.43.
    `,
    fixed: `
- Default values for wifi with SSID bioconnect have been removed as the device firmware defaults to this SSID for diagnostic purposes.

- In some cases, a device could come online but be missing provisioning certificates, impacting authentication and enrollment for newer customer accounts and devices. This issue should now be fixed.

- When a device's wifi link was online but the underlying internet connection was offline over a long period, there could be a memory leak issue. This has been fixed.

- Periodically the safe alarm buzzer could seemingly sound for no reason if the safe was presented with a card to be read. This would happen only occasionally and is now resolved.

- In rare instances, there could be missed logs and issues with offline authentication for users in “+PIN” or “fingerprint” modes, where they would be incorrectly prompted for enrollment. This was due to the way some information was stored in local flash storage and has been resolved.

- Users authenticating on a safe with Card/UserID/Fingerprint authentication method, should be prompted to optionally enroll a fingerprint. This was getting skipped and fingerprint enrolment was not getting prompted but just being authenticated - this is now fixed and users should now be prompted.

- Medixsafes equipped with HID readers could sometimes be impacted by an issue where PIN entry would appear delayed or missed entirely. This was happening due to a delay in transmission of the Wiegand bits which would sometimes cause two or more key presses to be processed as merged instead of separate bits. This issue should now be resolved.

- Attempting to change the Authentication Method could fail and cause a boot and database loading error, this has been fixed. The safe will not process new sync requests or replies until storage is re-initialized.

- A race condition causing a crash on the device and reboot if WiFi network connectivity was lost during a device sync has been fixed. This issue also impacted some offline application logs.

- Device account id setting would get reset to 0 during a configuration update. This has been fixed, although it had no direct impact on customers.

- Fixed an issue that would cause a deadlock during bad network conditions. This specific issue occurred when the device could connect to a local network but could not reach the public Internet for long periods.

- First enrolled fingerprint credential after the local database is wiped wouldn’t authenticate. This has now been rectified.

- If a fingerprint enrolment timed out, the device could get into a temporarily locked state to prevent further enrolments. This has been fixed to ensure the timeout period avoids a race condition between multiple processes.

- Fixed a very rare race condition causing a crash if an attempt to send a message was done while the internal messaging service was being shut down.

- Fixed a very rare race condition that could cause a firmware upgrade to fail due to an unnecessary reboot.

- Mobile Step-up requests will immediately time out if the device loses it’s network connection instead of waiting the entire configured timeout period.

- Fixed a rare race condition causing the device to restart after a firmware upgrade has been finalized.

- The DHCP client on the device could fail and stay in a disconnected state if a certain set of conditions were met, preventing reconnection to a WiFi network.

- In the very specific case when the internet connection is down but the DNS is still resolving, a memory leak could occur if the Medixsafe firmware is on 1.2.20 or higher during client restarts.

- Fixed a bug where when a user fails a mobile step-up verification, there is still a wiegand output signal. This does not impact Medixsafe customers.

- General device logging improvements have resulted in better device performance and reporting.
    `,
    improved: `
- For Link devices, a local credential database is periodically synced from the cloud. To prevent incomplete database states due to interrupted network connections during synchronization, we've implemented a rollback feature. This feature automatically restores credentials present before the latest synchronization attempt.

  - Device will consider an interruption in connectivity near to a device synchronization as an incomplete synchronization and it will automatically reattempt the synchronization within a minute of it re-establishing connectivity.

  - If interruption occurs on an initial synchronization attempt, the device will request a full re-synchronization on the subsequent sync.

- For devices set on Safe operation mode - the time Medixsafe waits for a cloud response has been reduced to 3 seconds from 45 seconds. This change aims to avoid users being stuck waiting for a cloud response during poor connectivity.

  - Note that this means Safe devices cannot use Mobile Authenticators for mobile step-up security. This can be changed by a BioConnect member upon request.

  - If the cloud does not respond within 3 seconds, the device will use its local database for authentication.

- If your safe is not added to an account, the LCD on the safe will display the current readerID (MAC Address), aiding administrators in adding devices to Link more easily.

- BioConnect Support team is now able to help open a safe remotely that has a non-functional HID or ICT reader. While this is for absolute emergencies and unlikeliest of scenarios, this will help our support team help customers who are having issues entering the emergency OTP to open the safe. BioConnect team will still need the displayed OTP for the device only accessible to the user.

- Updates have been made to the logic of x509 certificates used for device provisioning and security.

- There is now a periodic check to ensure there are no unreported offline log entries for extra precaution.

- Optimizations to the LCD driver memory usage and improved responsiveness of the display. While it didn’t lead to incorrect behaviour, previously it could cause a detectable delay in updating the display as buttons are pressed, this has also been fixed.

- Firmware Updates focused on general stability improvements

  - Firmware update process is more resilient to minor networking issues due to a retry mechanism.

  - Local logging is now automatically activated before firmware updates start and is maintained until  the firmware is finalized to help BioConnect team members navigate through any potential upgrade issues.

  - The time it takes to do firmware updates has been optimized. Note that any pending PIN and Fingerprint enrolments will be automatically cancelled during a firmware upgrade.

  - Device synchronization requests during a firmware upgrade are now prevented.

  - The device will attempt to close and reconnect its connection to the cloud after a period of missed responses. This change addresses periods of very poor network connectivity where the device could stay silently disconnected for long periods.
  `,
  },
  {
    version: '1.6.5',
    date: 'February, 2024',
    fixed: `
- A minor race condition has been addressed, which could previously cause a timeout when a safe was requesting a device sync.
    `,
    improved: `
- We've enhanced the logic related to unassigned users, particularly when utilized alongside an Access Control user sync.

- Internal logging updates. No impact to users.
    `,
  },
  {
    version: '1.6.4',
    date: 'January, 2024',
    fixed: `
- Internal logging updates. No impact to users.
    `,
  },
  {
    version: '1.6.3',
    date: 'December, 2023',
    fixed: `
- In rare instances, customers would occasionally encounter an incorrect user list when rapidly refreshing the User Management page multiple times. This issue stemmed from a race condition involving various factors, which has now been rectified.

- The Add Credential Modal occasionally displayed invalid future timestamps, impacting the addition of credentials to users. Transaction times will now only display relevant timestamps. This fix required changes to both the cloud software and a firmware update.

- Adding and removing users from a group could encounter a caching problem, causing users to authenticate against older data in some cases. This issue has been resolved.
    `,
  },
  {
    version: '1.6.2',
    date: 'November, 2023',
    added: `
  - We have added new links to helpful enrollment videos to the User Management page. These short how-to videos show steps to add Cards, User Ids, PINs and Fingerprint to a user.

  - Consent Tracking has been enabled for devices operating on a Fingerprint authentication method. It is important in some provinces/states to get consent from users prior to using biometric authentication. An admin will have to check this box when adding a new device or changing an existing with a Primary Authentication method that includes Fingerprint.
    `,
    improved: `
  - The language in the popup box to manually update an outdated device's firmware has been improved. We felt it was important for customers to know that during a firmware upgrade, the device will need to re-synchronize all users which, if interrupted, can prevent safe access. Therefore, we have added a note highlighting the importance of network stability and giving time for a full synchronization.
    `,
  },
  {
    version: '1.6.1',
    date: 'October, 2023',
    fixed: `
  - In some cases, offline events involving Two Credential Authentication were being incorrectly reported as failed when it was successful. This has been rectified.

  - In rare cases, access events were not displaying Authentication method in Access Log page. This has been rectified.
    `,
  },
  {
    version: '1.6.0',
    date: 'October, 2023',
    added: `
  - New Biometric Fingerprint Authentication

    - Safe or Key Cabinet mode devices can now utilize fingerprint authentication, provided the safes are equipped with our biometric module offering. BioConnect Link can support up to 5000 templates with the highest level of encryption.

    - New Primary Authentication Methods - We have expanded the options for primary authentication methods for Safes. You can now configure your safe with three new methods: Card/UserID/Fingerprint, Card/UserID + Fingerprint, and Fingerprint Only (which disables other authentication methods). Please ensure your safe has a fingerprint scanner before enabling any of these modes.

    - For customers wishing to use fingerprint authentication, please note that a Card or UserID must already exist for the user. Enrolling a fingerprint requires prior assignment of a Card or UserID via the web console.

    - Biometric templates for a user can now be deleted using the Delete Credential function.

  - Support of a LCD Screen

    - Safes are now available with LCD Screens that display helpful status messages, information such as device MAC Address, firmware version, usernames, errors, and alerts. The LCD is automatically configured by BioConnect Link, requiring no action from the user.

  - Security Update

    - We have continued to enhance the security of our patented encryption technology, both on the Link microcontroller and in the BioConnect cloud. It's important to note that no raw biometric templates are stored in our cloud. All biometric templates are encrypted and cannot be decrypted in the cloud.

  - We have also added the capability to delete PINs for a user within the Delete Credential function. This action will remove all PINs associated with any primary credential (card or UserID) assigned to the user.

  - Device firmware can now be manually updated if it is out of date. Administrators can see the option to update the firmware to the latest version via the Device Management menu for devices running older firmware that are currently online.

  - New firmware version 1.2.12 released to support the major updates above. This version includes numerous major and minor fixes and enhancements.
    `,
    fixed: `
  - In some cases, users who were deleted or deactivated in an ACM (Access Control Manager) for customers using our ACM sync feature may not have been accurately updated on the Link platform.

  - Fixed an issue where some offline transactions may get stuck in a Pending  state when they are uploaded to the Link console.

  - Online devices will now correctly check with the cloud to verify the validity of presented credentials, and if valid, initiate PIN enrollment as expected. Previously PIN enrollment was only possible after the credential had been synced down. Note, the device will still not allow for PIN enrollment on devices that are not Enabled,

  - In cases where an invalid credential was entered while in offline mode with Two Credential Authentication enabled, users were incorrectly prompted for a second credential.

  - In rare cases, a race condition could occur where a false “door open” alarm would be generated. This collision should no longer occur.

  - We have fixed API behavior inconsistencies that existed in specific requests related to Device creation.

  - We have also addressed many inconsistency issues with various fields in the Device Management, Group Management, and User Management pages.

  - Time-handling throughout the code has been improved to avoid situations where invalid past/future time stamps are accidentally assigned to events.
    `,
    improved: `
  - We have made significant improvements to the device's connection to cloud clients, especially in situations where the network is operational but the TCP socket drops due to network instability. With these enhancements, devices will now typically reconnect within 2 to 3 seconds, a substantial improvement from the previous wait time of 20 to 30 seconds before attempting reconnection.

  - We have improved the transmission of offline events by the device to better account for any errors and their impact on other threads.

  - We have implemented further improvements in Link to optimize the support for HID readers.

  - Data Visualization  Update for User Management

    - A new column has been added to show the credentials available and currently enrolled for a user.

    - The Mobile Verification column now clearly indicates whether a user has mobile step-up authentication enabled.

    - The Email and Phone number columns have been removed due to low usage, but this information can still be found in the Details tab for a selected user.

  - Data Visualization Update for Access Logs

    - Remote Authorization events now appear similarly to regular authentication events, including the user who generated the One-time Passcode for access.

    - Door forced open/Held open events are now included in Access Logs.

    - Events are now grouped together to better track all aspects of a transaction rather than just the authentication. This is particularly helpful to see Two Credential Authentication, as well as Door Open/Close events where available.

    - The credential type (e.g., Card, UserID, Fingerprint, PIN) used for authentication at an access point is now included where possible on transactions.

    - Please note that devices on older firmware and transactions completed before the release of Link 1.6 will not be able to take advantage of all the new features.

  - The CSV export feature in Access Logs has also undergone a complete overhaul

    - Authentication events are grouped together, including sub-events such as Door Open, Door Close, and Door Held Open.

    - Door Forced Open events are now included in the CSV export.

    - A new column, EventId, has been added to help group transactions within the CSV. This field is unique within the exported CSV.

    - EventIdentifier is a new column that is unique across all transactions. This can be helpful for diagnostic purposes.

    - Please note that devices on older firmware and transactions completed before the release of Link 1.6 will not be able to take advantage of all the new features.

    - There is now a note highlighting that the CSV export times are in UTC time zone.

  - The Delete Group function now requires a confirmation request before deletion.

  - We have enhanced the performance of Device Sync, resulting in faster synchronization during full syncs.

  - We've updated the behavior of device telemetry data to more accurately reflect changes. This has resolved an issue where information on the Device Management page was not updating as expected.

  - The device name has been added at the top of the Device Settings modal for clarity.

  - Mobile Verification schedule time has been changed to be a 12 hour clock with an am/pm selector.

  - Cards or UserIDs added for a user are no longer automatically enabled for Mobile Verification.

  - Terms and Conditions page has been updated.
    `,
  },
  {
    version: '1.5.4',
    date: 'August, 2023',
    added: `
  - We have introduced new helpful links to assist you in the process of adding devices. You can now find a link to the Network Ports required for your Link device in the Device Management section as well as on the login page. This information is also available in your Link Welcome Email!
    `,
    fixed: `
  - A new firmware version (1.1.10) has been released, incorporating improvements to how the device manages offline logs and alerts. In rare cases, devices would attempt to continuously resend alerts, resulting in numerous email alerts for past events once the device is back online. This issue has been resolved and should no longer occur.

  - A memory-related issue that affected certain devices has been successfully addressed and resolved.
    `,
    improved: `
  - We have made enhancements to the Remote Authorization modal to now support the addition of new HID Signo readers on our Safes.

  - Some modifications have been made to the text of the Beta SMS mobile authenticator option.
    `,
  },
  {
    version: '1.5.3',
    date: 'August, 2023',
    fixed: `
  - Internal update to cloud messaging structure.
    `,
  },
  {
    version: '1.5.2',
    date: 'June, 2023',
    fixed: `
  - In some offline authentication scenarios, logs could be incorrectly deleted if a cloud connection was established, dropped and then re-established during the short time period when device was transmitting logs to the BioConnect Cloud. This has now been fixed and device will only delete logs on successful transmission of events to cloud.
    `,
  },
  {
    version: '1.5.1',
    date: 'June, 2023',
    fixed: `
  - Some customers may have experienced an issue where PIN enrolled on one device did not properly sync to other devices if the PIN enrollment was not done immediately after the associated Card or UserID was added for the user
    `,
  },
  {
    version: '1.5.0',
    date: 'May, 2023',
    added: `
  - New Role added for users - Group Administrator

    - Group administrator is a new limited administrator role that is meant to manage different access group without being able to do account level configuration changes

    - This new administrator has limited access rights compared to an Account Administrator and is primarily focused on day to day management of Groups within Link

    - To be an administrator for a Group, the user must be created as a Group administrator and also be assigned to the group by a higher administrator role

    - Group Administrators assigned to a Group can add or remove users from the Group and update network & authentication settings for devices assigned to their Group

  - New Feature to support Two Credential Authentication for Safe & Key Cabinet mode

    - When enabled, two different credentials must be entered within 60 seconds to authenticate successfully. These authentication events will each follow the primary authentication method  set in Device Settings (i.e. Card Only, Card + Pin etc)

    - Reader LED pattern will blink blue-red-blue while waiting for the second authentication when in this mode

  - New feature of Remote Authorization lets administrators generate a code that when entered on a Safe device with a keypad will allow access. This will work as long as the safe is powered on. This can be used for emergencies to get access to the safe and can be triggered by administrators from Device Management

  - User Management now lets you delete specific credentials assigned to users from the new Delete Credential option

  - New feature to support alternative WiFi connectivity. Devices on the latest firmware can now support multiple WiFi configurations added from the web console

    - Devices will now have a primary WiFi connection and a secondary connection that it can attempt to connect to if it loses internet connectivity. Link hardware will attempt to connect to the strongest available connection

  - Firmware Specific Updates (version 1.1.6) - these changes should have been pushed directly to your hardware

    - A new Mobile Setup Wizard has been added that can be used to connect to your Link devices (i.e. safes or cabinets) for the first time

      - Mobile setup can be launched by powering on your device, and using your mobile (or laptop) device to connect to the device’s generated wifi access point

      - Once launched, you can use your browser to configure network and account information to connect your device to BioConnect Cloud platform

      - Once configured, this portal will no longer be available

    - Device can now be rebooted by pressing *##### (or X[↲] [↲][↲] [↲][↲]). 5 times pressing the [↲] or # key
    `,
    fixed: `
  - Offline and 1:1 scenarios with pin authentication exhibited unexpected behavior on some devices. This has now been fixed with the latest firmware

  - A toggle for when to expect a facility code from a credential was getting overwritten which impacted some customers at the door. This was resolved as part of a hotfix.

  - Offline email alerts sometimes showed the incorrect timestamp - this has now been rectified to show the correct timestamp of the transaction

  - A condition that could periodically trigger “door open” alarms during device startups has now been fixed

  - During PIN enrolment, a user could sometimes bypass entering the verification pin by pressing Enter twice instead of confirming their chosen PIN. This has been fixed.

  - Fixed an issue where the device was not properly recording offline entry of invalid PINs
  `,
    improved: `
  - The user interface around Device Network settings has been redone to provide more information to administrators of connected networks

  - Whenever a device configuration change is attempted, a new feedback screen will show if the configuration was successfully sent to the device

  - Devices will also now attempt to connect to a default troubleshooting wifi if connectivity cannot be established to the provided networks (this can be found in Device Network Settings)

  - Eliminated auditory feedback whenever a firmware is upgraded on a device, this caused some confusion to customers

  - The safe buzzer should now sound for a moment if a user attempts to enter a PIN digit but the safe is configured to a primary authentication method that is Card-only

  - Pin Enrollment reader LED colours have been changed to flash blue/green as opposed to just blue to distinguish between normal Card + Pin entry versus Enrollment

  - Many device resiliency improvements around network connectivity to increase stability and handle network loss better

  - Improvements to the device sync from BioConnect cloud to devices have been implemented to ensure syncs are faster and prioritizes authentication events
    `,
  },
  {
    version: '1.4.6',
    date: 'April, 2023',
    fixed: `
  - Fixed page controls when the number of Groups exceeded beyond default count of 10.
    `,
  },
  {
    version: '1.4.5',
    date: 'March, 2023',
    fixed: `
  - Fixed an issue that caused some customers to experience inconsistent behavior at the door due to an account-wide setting getting impacted regarding facility code.

  - Fixed an issue where administrators weren’t being synced when a device was added to a group but no users were added.
    `,
  },
  {
    version: '1.4.4',
    date: 'November, 2022',
    fixed: `
  - Fixed an issue where HID Mobile credential integration was not working as intended when a user had many invitation codes, devices, credentials added under their profile.
    `,
  },
  {
    version: '1.4.3',
    date: 'August, 2022',
    added: `
  - Minimum firmware version for all devices has been updated to 1.0.43. This brings increased resiliency in device startup, some improvements/fixes to offline logging and functionality, as well as introduction to new wifi settings. bioconnect/bioconnect SSID and Password hotspot can be created at any time and Link hardware will attempt to connect to that in the event of a connection loss.
    `,
    fixed: `
  - Alert emails for Door Held Open and Door Forced Open events were not firing for certain scenarios where the access point (Server Cabinet or Key and Safe Cabinet Operation modes only) was not in a group. This has been fixed.
    `,
  },
  {
    version: '1.4.2',
    date: 'June, 2022',
    added: `
  - New Device Mode for Keys and Safe Cabinets - In addition to Doors and Server Cabinets. Link now offers Keys and Safe Cabinets. This mode is intended to be used for safeguarding Key Cabinet Lockers or safes used for protecting critical  small items such as narcotics

  - Support of new Authentication Methods for new mode - Support of Pin modes, User ID along with usual Card and Mobile authentication

  - Support of New alerts - including Door Held Open. Door Held Open time can be configured via Settings. This setting is for new Safe and Key Cabinet  mode only

  - Support for Facility Code  -this must be done during Account creation. Please let your Bioconnect contact know if you would like facility code to be respected for any cardholders before your Link  account is provisioned

  - New hotspot for Device provisioning update - BioConnect Link device on latest firmware can provide new hotspot that shows up as “BIOC_MACADDRESS”. Connecting to this hotspot will allow provisioning of device including providing network information, operation modes - the device must still be added on the account side first
  `,
    fixed: `
  - Fixed bug where previously deleted users would show up as Null on Transaction Logs. This has been corrected so that user name will now show up in the past authentication events after being deleted

  - Fixed a workflow bug around activation of HID credentials where adding a user in Origo first would not properly active HID Mobile credential

  - Minor UI Date fix on old release note
    `,
    improved: `
  - Text changes to better reflect the new addition of operation modes across the Link portal
    `,
  },
  {
    version: '1.4.1',
    date: 'March, 2022',
    fixed: `
  - Minor changes around integration between HID Origo Portal and Link
    `,
  },
  {
    version: '1.4.0',
    date: 'January, 2022',
    added: `
  - Added support for HID Mobile Credential while using BioConnect Mobile - BioConnect Link can now be used to administer HID Credentials for a fully mobile credential based entry. Users can now tap their mobile and do a biometric authentication for entry
    `,
  },
  {
    version: '1.3.1',
    date: 'April, 2021',
    added: `
  - Device Management Settings modal has now been updated to show Mac address (previously visible only on mouse hover) and Firmware version of device

  - Configurable Access Logs - Access logs can now be viewed across any customizable time period. Admins can select to view access logs across all devices on account for any specified Start and End date. Quick options are also available for Past 24 hours, Past 7 days and Past 30 days

  - Export to CSV - From the Access Logs page, admins can now export to CSV. Transactions must exist within the designated time period for export option

  - New firmware version - all devices will be updated to FW version 1.0.23 upon device restart. This firmware changes port usage in cabinet mode. Door 1 and Door 3 functionality and wiring is reversed. Wiring diagrams on install guide has been updated to reflect this. Changes to DHCP caching to improve stability when using Static IP_Changes to NTP to address a corner scenario when using Static IP with a blocked NTP server
    `,
    fixed: `
  - Fixed an issue that existed with Static IP configuration of devices specifically using a Static DNS

  - Fixed an issue with Cabinet offline mode cache
    `,
    improved: `
  - Device settings page now shows actual IP of the device to account for scenarios where the device rolls back to a previously working configuration. Previously, it used to show whatever Static IP settings were sent to the Link device rather than the actual

  - Error message will now indicate to customers on unsupported web browsers that for best experience to please use Google Chrome or Firefox

  - Password validation has been made friendlier to indicate in real-time if password requirements are met on Reset Password, New Admin password and Change password screens
  `,
  },
  {
    version: '1.3.0',
    date: 'January, 2021',
    added: `
  - BioConnect Link is now integrated with the BioConnect Enterpise platform. This will enable use of mobile authentication at the door with all Suprema Devices. All transactions from BioConnect Enterprise will be available in your logs

  - New device firmware (1.0.21) fixes a race-condition when setting a static IP for ethernet while WiFi is active on a device

  - New device firmware (1.0.21) allows for factory reset of devices by wiring Door0 LED into Door0 IN

  - Link device now allows for connecting to a diagnostic hotspot - device will try to connect to a preset hotspot 'Bioconnect' by default
  `,
  },
  {
    version: '1.2.5',
    date: 'September, 2020',
    added: `
  - Teams Integration for webhooks - BioConnect Link now supports transaction events at the door pushed real time to Microsoft Teams. This supports both Wellness Declaration feature as well as normal transactions
    `,
    improved: `
  - Newly synced users from an ACM are no longer enabled for 2FA by default. All newly added users are disabled for 2FA and have to be enabled manually for them to receive stepup. This is to assist customers who want to sync their entire user base but want 2FA rolled out in phases

  - Minor UI/UX changes to existing features and feedback mechanism
    `,
  },
  {
    version: '1.2.4',
    date: 'September, 2020',
    added: `
  - Customize and configure your wellness survey questions before people enter your premises. BioConnect Link now allows writing up to 8 questions that can be set as a requirement to be answered before access is granted
    `,
    fixed: `
  - Fixed an issue where offline transaction times were getting incorrectly reported on devices
    `,
  },
  {
    version: '1.2.3',
    date: 'July, 2020',
    added: `
  - Added the ability for further customization of BioConnect Wellness Declaration. Administrators can now select the second factor method of authentication to enforce after wellness is completed by an end user

  - Firmware version of BioConnect Link hardware can now be upgraded to 1.0.19. This will happen automatically. This update adds changes to device configuration parameters, response structure as well as future proofing for an upcoming BioConnect Link feature
  `,
    fixed: `
  - Validation for Okta and PingID authenticators work as intended. Only valid configuration keys will now be accepted when adding an authenticator
    `,
    improved: `
  - Minor improvements around authenticator settings and user management with ACM Sync
    `,
  },
  {
    version: '1.2.1',
    date: 'June, 2020',
    added: `
  - BioConnect Wellness Declaration is now live. In response to operational changes resulting from COVID-19, BioConnect offers the ability to monitor individuals who may pose a potential risk to the health of your facility

  - While Wellness Declaration is enforced, a survey will be sent on the first card scan of a day for the user, or completed voluntarily from the user’s BioConnect Mobile app

  - A notification message of a failed Wellness Declaration can be sent in real time to a user-specified Slack channel, as well as to a specified notification email

  - Added Slack as a form of real time updates. This currently works in sync with the Wellness Declaration functionality but can be updated to all access events in the future
    `,
    fixed: `
  - Fixed an issue where a 3rd party authenticator was unable to be deleted if assigned to a group. Deleting an authenticator assigned to a group will set it to card only

  - Fixed an issue where user search behaved inconsistently when searching by full name

  - Removed test requests generated from the BioConnect Link web console from showing up in Physical Access log events
    `,
    improved: `
  - Ability to update account wide notification email
    `,
  },
  {
    version: '1.2.0',
    date: 'April, 2020',
    added: `
  - Okta Verify is now a new authenticator that can be used for second factor authentication at the door

  - BioConnect Link now supports multiple authenticators. If desired, each group of users and devices within BioConnect Link can now operate with its own unique or individual supported two factor authenticator

  - Physical Access event log is now available in the BioConnect Link Manager. These logs show all access events coming from an account's BioConnect Link devices

  - Webhook functionality, to allow admins to push real time transaction data to anywhere via a URL, such as to a SIEM or even company communication channels such as Slack
    `,
    fixed: `
  - Verified and updated functionality where some Link devices were not automatically updating to the latest firmware upon coming online

  - Fixed an issue with input validation on reset password functionality

  - Copy changes to Getting Started Guide
    `,
    improved: `
  - Searching devices is now available in Device Management, as well as the ability to search users and devices when adding to a group

  - Other minor general UI/UX improvements
    `,
  },
  {
    version: '1.1.0',
    date: 'March, 2020',
    fixed: `
  - SMS step-up request messaging was updated to remove a double period

  - Fixed an issue where specific door ports on the Link device would show up as inactive if on a different page than Door 0 of the Link device

  - Fixed an issue on groups where selected user count in Groups was not consistent across pages

  - Fixed an issue where incorrect parameters were being sent to Link device under certain conditions
    `,
    added: `
  - New Getting Started Panel to help guide new users through BioConnect Link Manager and most frequently used functionality

  - BioConnect's mobile Biometric authenticator can now be used for second factor authentication at the door - completely free!

  - PingID is now a new authenticator that can be used for second factor authentication at the door
    `,
    improved: `
  - ACM Sync, when activated, can pull phone numbers and email from supported ACM partners

  - Enhancements and general hardening of group logic

  - Multiple instances where copy was updated on the Link web portal to be more intuitive

  - Updated alerting logic for cabinet mode to send alerts only for warning events, all other notifications are still tracked as Events

  - Static IP can now be set for Ethernet and WiFi, there is now a toggle to switch between the two settings
    `,
  },
  {
    version: '1.0.0',
    date: 'February, 2020',
    fixed: `
  - Fixed an issue where the reporting of offline events could be delayed in some circumstances, after the network connection was restored

  - Eliminated a possible race condition where a card scan could be delayed if it arrives at the same time that the hardware watchdog is performing a system check
    `,
    added: `
  - SMS can now also be used for second factor authentication at the door.

  - User management, added emails and phone number fields

  - User management, edit and modify user details
    `,
    improved: `
  - Device Management page has been completely redone

  - Various small tweaks and improvements in UI and UX throughout

  - Several updates to improve how the LEDs display locked/unlocked/alarm status when the BioConnect Link device is operating in Cabinet mode

  - Update to how the LED is managed when the BioConnect Link is controlling door access: the "step-up pending" flash will immediately terminate if a user clicks "deny" on a step-up request
    `,
  },
]

const releaseNotesMixin = {
  data() {
    return {
      releasesContent: RELEASES,
    }
  },
}

export default releaseNotesMixin
